import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { QrCode, TicketPercent } from "lucide-react";
import type { RedeemedCouponRecord } from "@/utils/couponFormat";
import {
  formatCouponExpiry,
  isRedeemedCouponExpired,
  isRedeemedCouponUsed,
} from "@/utils/couponFormat";

interface StudentRedeemedCouponCardProps {
  record: RedeemedCouponRecord;
  onShowQr?: (record: RedeemedCouponRecord) => void;
}

/**
 * Redeemed coupon card mirroring the RN `RedeemedCoupon`: brand image, name,
 * expiry and the action the coupon supports — show the QR code for the shop to
 * scan, copy a promo code, or contact the business.
 */
const StudentRedeemedCouponCard: React.FC<StudentRedeemedCouponCardProps> = ({
  record,
  onShowQr,
}) => {
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = useState(false);
  const language = String(i18n?.language ?? "en");
  const isZh = language.toLowerCase().startsWith("zh");

  const couponInfo = record?.coupon_info ?? {};
  const name = String(couponInfo.coupon_name ?? "");
  const image = String(couponInfo.coupon_image ?? "");
  const companyName = String(
    couponInfo.user_info?.business_profile?.company_name ?? "",
  );
  const contactNumber = String(
    couponInfo.user_info?.business_profile?.contact_number ?? "",
  );
  const promoCode = String(couponInfo.promo_code ?? "");
  const scanByQrCode = Boolean(couponInfo.scan_by_qr_code);
  const used = isRedeemedCouponUsed(record);
  const expired = isRedeemedCouponExpired(record);
  const expiryLabel = formatCouponExpiry(record?.expiry_date, language);
  const terms = String(
    isZh
      ? couponInfo.terms_conditions_zh_hant || couponInfo.terms_conditions_en || ""
      : couponInfo.terms_conditions_en || couponInfo.terms_conditions_zh_hant || "",
  ).trim();

  const whatsappHref = contactNumber
    ? `https://wa.me/${contactNumber.replace(/[^0-9]/g, "")}`
    : "";

  const handleCopyPromoCode = () => {
    navigator.clipboard?.writeText(promoCode).catch(() => {});
    setCopied(true);
  };

  return (
    <article className="flex h-full flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition hover:shadow-md">
      <div className="relative">
        {image ? (
          <img
            src={image}
            alt={name || t("Coupon")}
            loading="lazy"
            className="h-32 w-full object-cover"
          />
        ) : (
          <div className="flex h-32 w-full items-center justify-center bg-gray-50">
            <TicketPercent size={28} className="text-gray-300" />
          </div>
        )}
        {used && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/55">
            <span className="-rotate-6 rounded-lg border-2 border-white bg-white px-4 py-1.5 text-sm font-extrabold tracking-wider text-red-500">
              {t("USED")}
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col p-4">
        <h3 className="line-clamp-2 text-sm font-bold leading-5 text-gray-900">
          {name || t("Coupon")}
        </h3>
        {companyName && (
          <p className="mt-0.5 line-clamp-1 text-xs text-gray-500">{companyName}</p>
        )}
        {promoCode && (
          <p className="mt-1 break-all text-xs font-semibold text-[#ffa217]">
            {t("Promo Code")}: {promoCode}
          </p>
        )}
        {expiryLabel && (
          <p className="mt-1 text-xs text-gray-400">
            {t("Valid until")}: {expiryLabel}
          </p>
        )}
        {terms && (
          <p className="mt-2 line-clamp-2 text-xs leading-4 text-gray-400">{terms}</p>
        )}

        <div className="mt-auto pt-3">
          {expired ? (
            <span className="inline-flex w-full items-center justify-center rounded-xl bg-gray-100 px-4 py-2 text-xs font-bold text-gray-400">
              {t("EXPIRED")}
            </span>
          ) : used && !scanByQrCode ? (
            <span className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl bg-gray-100 px-4 py-2 text-xs font-bold text-gray-400">
              <QrCode size={14} />
              {t("USED")}
            </span>
          ) : scanByQrCode && promoCode ? (
            <button
              type="button"
              onClick={handleCopyPromoCode}
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-[#ffa217] bg-white px-4 py-2 text-xs font-bold text-[#ffa217] transition hover:bg-[#fff8f0]"
            >
              {copied ? t("Copied") : t("Get Promo Code")}
            </button>
          ) : !scanByQrCode ? (
            <button
              type="button"
              onClick={() => onShowQr?.(record)}
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl bg-[#ffa217] px-4 py-2 text-xs font-bold text-white transition hover:bg-[#f59200]"
            >
              <QrCode size={14} />
              {t("Show QR code")}
            </button>
          ) : whatsappHref ? (
            <a
              href={whatsappHref}
              target="_blank"
              rel="noreferrer"
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl bg-[#ffa217] px-4 py-2 text-xs font-bold text-white transition hover:bg-[#f59200]"
            >
              {t("Contact Business")}
            </a>
          ) : null}
        </div>
      </div>
    </article>
  );
};

export default StudentRedeemedCouponCard;
